import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, RefreshCcw, Trash2 } from 'lucide-react';
import B2BLayout from '../../../components/b2b/B2BLayout';
import { ConfirmModal } from '../../../components/ui';
import { useAppContext } from '../../../context/AppContext';
import { ErrorBanner, formatDateTime } from '../components/PersonaShared';
import { personaFigmaApi } from '../services/personaService';
import '../styles/persona.scss';

const TEST_FIGMA_RETURN_PATH_KEY = 'reopsai:test-create-figma-return-path';
const FIGMA_SYNC_ERROR_MESSAGES = {
  figma_permission: '해당 링크의 파일 권한을 확인해주세요',
  figma_sharing: '해당 링크의 파일 공유 설정을 확인해주세요',
  missing_flow: '파일 내 프로토타입 Flow가 연결되어 있는지 확인해주세요.',
  duplicate: '이미 추가된 URL 주소입니다.',
  invalid: '올바른 Figma 파일 URL 형식이 아닙니다.',
};

function getFigmaSyncErrorMessage(error, fallback) {
  const code = error?.data?.status || error?.data?.code || error?.code;
  if (code && FIGMA_SYNC_ERROR_MESSAGES[code]) {
    return FIGMA_SYNC_ERROR_MESSAGES[code];
  }
  return error?.message || fallback;
}

function extractFileKey(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  try {
    const url = new URL(text);
    if (!/(^|\.)figma\.com$/i.test(url.hostname)) return '';
    const match = url.pathname.match(/^\/(?:file|design)\/([^/]+)/i);
    return match?.[1] || '';
  } catch {
    return '';
  }
}

function FigmaSettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppContext();
  const params = new URLSearchParams(location.search);
  const [status, setStatus] = useState(null);
  const [files, setFiles] = useState([]);
  const [flowsByFile, setFlowsByFile] = useState({});
  const [fileInput, setFileInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [refreshingFileId, setRefreshingFileId] = useState(null);
  const [accountDisconnectOpen, setAccountDisconnectOpen] = useState(false);
  const [disconnectingAccount, setDisconnectingAccount] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [deleteTargetFile, setDeleteTargetFile] = useState(null);
  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [modalError, setModalError] = useState('');
  const [error, setError] = useState(params.get('error') || '');
  const connectedParam = params.get('connected');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statusData, filesData] = await Promise.all([
        personaFigmaApi.getStatus(),
        personaFigmaApi.listFiles(),
      ]);
      setStatus(statusData);
      setFiles(filesData);
      const flowEntries = await Promise.all(
        filesData.map((file) => (
          personaFigmaApi.listFlows(file.id)
            .then((flows) => [file.id, flows])
            .catch(() => [file.id, []])
        ))
      );
      setFlowsByFile(Object.fromEntries(flowEntries));
    } catch (err) {
      setError(err.message || 'Figma 연결 정보를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (connectedParam !== '1') return;
    const returnPath = window.sessionStorage?.getItem(TEST_FIGMA_RETURN_PATH_KEY) || '';
    if (!returnPath.startsWith('/tests/new')) return;
    window.sessionStorage?.removeItem(TEST_FIGMA_RETURN_PATH_KEY);
    navigate(returnPath, { replace: true });
  }, [connectedParam, navigate]);

  const handleConnect = async () => {
    setError('');
    try {
      const url = await personaFigmaApi.getConnectUrl();
      if (url) window.location.assign(url);
    } catch (err) {
      setError(err.message || 'Figma 연결 URL을 가져오지 못했습니다.');
    }
  };

  const handleDisconnect = async () => {
    setAccountDisconnectOpen(true);
  };

  const confirmDisconnect = async () => {
    if (disconnectingAccount) return;
    setError('');
    setDisconnectingAccount(true);
    try {
      await personaFigmaApi.disconnect();
      setAccountDisconnectOpen(false);
      await loadData();
    } catch (err) {
      setError(err.message || 'Figma 연결 해제에 실패했습니다.');
    } finally {
      setDisconnectingAccount(false);
    }
  };

  const handleSyncFile = async (event) => {
    event.preventDefault();
    const figmaFileKey = extractFileKey(fileInput);
    if (!figmaFileKey) {
      setModalError('올바른 Figma 파일 URL 형식이 아닙니다.');
      return;
    }
    setSyncing(true);
    setError('');
    setModalError('');
    try {
      const figmaFileLink = fileInput.trim();
      await personaFigmaApi.syncFile({
        figma_file_key: figmaFileKey,
        figma_file_name: figmaFileKey,
        figma_file_link: figmaFileLink,
      });
      setFileInput('');
      setFileModalOpen(false);
      await loadData();
    } catch (err) {
      setModalError(getFigmaSyncErrorMessage(err, 'Figma 파일 연결에 실패했습니다.'));
    } finally {
      setSyncing(false);
    }
  };

  const handleRefreshFile = async (fileId) => {
    setRefreshingFileId(fileId);
    setError('');
    try {
      const refreshedFile = await personaFigmaApi.refreshFile(fileId);
      const flows = refreshedFile.flows || [];
      setFiles((prev) => prev.map((file) => (
        file.id === fileId ? { ...file, ...refreshedFile } : file
      )));
      setFlowsByFile((prev) => ({ ...prev, [fileId]: flows }));
    } catch (err) {
      setError(getFigmaSyncErrorMessage(err, 'Figma 파일 동기화에 실패했습니다.'));
    } finally {
      setRefreshingFileId(null);
    }
  };

  const handleDeleteFile = async (file) => {
    setDeleteTargetFile(file);
  };

  const confirmDeleteFile = async () => {
    if (deletingFileId) return;
    const file = deleteTargetFile;
    if (!file) return;
    const fileName = file.figma_file_name || file.figma_file_key || '선택한 Figma 파일';
    setDeletingFileId(file.id);
    setError('');
    try {
      await personaFigmaApi.deleteFile(file.id);
      setFlowsByFile((prev) => {
        const next = { ...prev };
        delete next[file.id];
        return next;
      });
      setDeleteTargetFile(null);
      await loadData();
    } catch (err) {
      setError(err.message || `"${fileName}" Figma 파일 연결 해제에 실패했습니다.`);
    } finally {
      setDeletingFileId(null);
    }
  };

  const isConnected = Boolean(status?.connected || status?.account);
  const account = status?.account || {};
  const displayName = account.figma_handle || user?.name || 'Figma 계정';
  const displayEmail = account.figma_email || user?.email || '-';
  const canSubmitFile = Boolean(fileInput.trim()) && !syncing;

  return (
    <B2BLayout hideCollapseButton={false} defaultCollapsed={true} hidePageHeader={true}>
      <main className="figma-settings-page">
        <header className="figma-settings-topbar">
          <h1>설정</h1>
        </header>
        <div className="figma-settings-body">
          <section className="figma-settings-content">
            <header className="figma-settings-heading">
              <h2>피그마 연동</h2>
              <p>Figma 계정을 연결하고 프로젝트 파일을 등록하여 리서치에 필요한 화면을 손쉽게 불러오세요.</p>
            </header>

            {connectedParam === '1' ? <div className="persona-success-banner">Figma 연결이 완료되었습니다.</div> : null}
            <ErrorBanner message={error} />

            <section className="figma-settings-card">
              <h3>내 계정 연결</h3>
              {loading ? (
                <div className="figma-settings-empty">Figma 연결 상태를 확인하고 있습니다.</div>
              ) : isConnected ? (
                <div className="figma-account-row">
                  <div className="figma-account-profile">
                    {account.figma_avatar_url ? (
                      <img src={account.figma_avatar_url} alt="" />
                    ) : (
                      <span>{String(displayName || 'F').charAt(0).toUpperCase()}</span>
                    )}
                    <div>
                      <strong>{displayName}</strong>
                      <p>{displayEmail}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="figma-outline-button"
                    onClick={handleDisconnect}
                    disabled={disconnectingAccount}
                  >
                    {disconnectingAccount ? '해제중' : '연결해제'}
                  </button>
                </div>
              ) : (
                <div className="figma-connect-empty">
                  <p>Figma 계정을 연결하면<br />프로토타입 혹은 화면을 불러올 수 있어요.</p>
                  <button type="button" className="figma-dark-button" onClick={handleConnect}>
                    <span className="figma-logo-dot" aria-hidden="true" />
                    Figma 계정연결
                  </button>
                </div>
              )}
            </section>

            {isConnected ? (
              <section className="figma-settings-card">
                <div className="figma-settings-card__head">
                  <h3>Figma 파일 연결</h3>
                  <button
                    type="button"
                    className="figma-dark-button"
                    onClick={() => {
                      setFileModalOpen(true);
                      setFileInput('');
                      setModalError('');
                    }}
                  >
                    <Plus size={18} />
                    파일 연결
                  </button>
                </div>

                {loading ? (
                  <div className="figma-settings-empty">파일 목록을 불러오고 있습니다.</div>
                ) : files.length === 0 ? (
                  <div className="figma-file-empty">
                    <strong>연결된 파일이 없습니다</strong>
                    <p>Figma 파일을 연결하여 플로우를 불러오세요.</p>
                  </div>
                ) : (
                  <div className="figma-file-list">
                    {files.map((file) => {
                      const flows = flowsByFile[file.id] || [];
                      const primaryFlow = flows[0];
                      const isRefreshing = refreshingFileId === file.id;
                      return (
                        <article key={file.id} className="figma-file-row">
                          <div className="figma-file-main">
                            <div className="figma-file-thumbnail">
                              {file.thumbnail_url ? (
                                <img src={file.thumbnail_url} alt="" />
                              ) : (
                                <span>thumbnail<br />IMG</span>
                              )}
                            </div>
                            <div className="figma-file-copy">
                              <div className="figma-file-name-row">
                                <strong>{file.figma_file_name || file.figma_file_key || '피그마 파일명'}</strong>
                                <em>{primaryFlow?.figma_flow_name || (flows.length > 0 ? `${flows.length} Flow` : 'Flow명')}</em>
                              </div>
                              <div className="figma-file-meta" aria-live="polite">
                                <span>마지막 업데이트: {formatDateTime(file.last_synced_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="figma-file-actions">
                            <button
                              type="button"
                              className="figma-outline-button"
                              onClick={() => handleRefreshFile(file.id)}
                              disabled={isRefreshing || Boolean(deletingFileId)}
                            >
                              {isRefreshing ? '새로고침 중' : '새로고침'}
                              <RefreshCcw size={18} className={isRefreshing ? 'is-spinning' : ''} />
                            </button>
                            <button
                              type="button"
                              className="figma-outline-button"
                              onClick={() => handleDeleteFile(file)}
                              disabled={deletingFileId === file.id}
                            >
                              {deletingFileId === file.id ? '삭제 중' : '삭제'}
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            ) : null}
          </section>
        </div>

        {fileModalOpen ? (
          <div
            className="figma-modal-backdrop"
            role="presentation"
          >
            <form
              className="figma-url-modal"
              onSubmit={handleSyncFile}
              onClick={(event) => event.stopPropagation()}
            >
              <header>
                <h2>Figma 파일 연결</h2>
              </header>
              <div className="figma-url-modal__body">
                <ul className="figma-url-rules">
                  <li>최소 1개 이상의 Flow(프로토타입)가 설정되어 있어야 합니다.</li>
                  <li>본인이 소유자(Owner) 권한을 가진 파일만 연결 가능합니다.</li>
                  <li>파일 공유 설정은 Anyone 및 can view로 설정되어야 합니다.</li>
                </ul>
                <label className={`figma-url-input${modalError ? ' is-error' : ''}`}>
                  <span>Figma URL</span>
                  <input
                    value={fileInput}
                    onChange={(event) => {
                      setFileInput(event.target.value);
                      setModalError('');
                    }}
                    placeholder="URL 주소를 입력해주세요"
                    autoFocus
                  />
                  {modalError ? <p>{modalError}</p> : null}
                </label>
              </div>
              <footer>
                <button
                  type="button"
                  className="figma-modal-secondary"
                  onClick={() => {
                    setFileModalOpen(false);
                    setFileInput('');
                    setModalError('');
                  }}
                  disabled={syncing}
                >
                  취소하기
                </button>
                <button type="submit" className="figma-modal-primary" disabled={!canSubmitFile}>
                  {syncing ? '연결 중...' : '연결하기'}
                </button>
              </footer>
            </form>
          </div>
        ) : null}

        <ConfirmModal
          isOpen={accountDisconnectOpen}
          type="warning"
          title="Figma 계정 연동을 해제하시겠습니까?"
          message="연동을 해제하더라도 생성된 리서치 결과물은 그대로 보존됩니다. 다만, 등록된 파일 리스트는 전부 초기화됩니다."
          showCancel
          canCloseOutside={false}
          onConfirm={confirmDisconnect}
          onCancel={() => {
            if (!disconnectingAccount) setAccountDisconnectOpen(false);
          }}
          confirmText="연동 해제"
          cancelText="취소"
          size="sm"
          align="dialog"
        />

        <ConfirmModal
          isOpen={Boolean(deleteTargetFile)}
          type="warning"
          title="연결된 파일을 삭제하시겠습니까?"
          message="파일을 삭제하더라도 이 파일로 생성된 리서치 결과는 사라지지 않습니다. 단, 리스트에서 삭제 시 해당 파일로 새로운 리서치 구성은 불가합니다."
          showCancel
          canCloseOutside={false}
          onConfirm={confirmDeleteFile}
          onCancel={() => {
            if (!deletingFileId) setDeleteTargetFile(null);
          }}
          confirmText="파일 삭제"
          cancelText="취소"
          size="sm"
          align="dialog"
        />
      </main>
    </B2BLayout>
  );
}

export default FigmaSettingsPage;
